import { motion } from "motion/react";
import { useRef, useState } from "react";
import type { PersonId } from "../data/people";
import { dayItems, dayTaskCount, isPastDay, tasksLabel } from "../lib/domain";
import { spring } from "../lib/motion";
import { dayInfo, dayOf, DAYS, timeOf } from "../lib/time";
import { cn } from "../lib/utils";
import type { Busy, Status, Task } from "../types";

/** Без обводок: полоска статуса слева; новая задача — в штриховку */
const CARD: Record<Status, string> = {
  open: "bg-muted text-muted-foreground shadow-[inset_3px_0_0_0_var(--ring)]",
  wait: "bg-[repeating-linear-gradient(135deg,var(--wait-soft)_0_7px,#fff3d1_7px_14px)] shadow-[inset_3px_0_0_0_var(--wait)]",
  work: "bg-card shadow-[inset_3px_0_0_0_var(--work),0_1px_2px_rgb(10_10_10/0.06)]",
  review: "bg-card shadow-[inset_3px_0_0_0_var(--review),0_1px_2px_rgb(10_10_10/0.06)]",
  rework: "bg-card shadow-[inset_3px_0_0_0_var(--rework),0_1px_2px_rgb(10_10_10/0.06)]",
  publish: "bg-card shadow-[inset_3px_0_0_0_var(--publish),0_1px_2px_rgb(10_10_10/0.06)]",
  done: "bg-card/60 text-muted-foreground shadow-[inset_3px_0_0_0_var(--done)]",
};
const WORD: Record<Status, string> = { open: "свободная", wait: "новая", work: "", review: "на проверке", rework: "правки", publish: "ждёт ссылок", done: "готово" };

/**
 * Неделя монтажёра по дням: задачи делаются день в день.
 * Новую задачу можно перетащить на другой день — постановщику уйдёт предложение.
 */
export function DayBoard({
  now,
  editor,
  tasks,
  busy,
  onOpen,
  onPropose,
  highlightId,
  compact,
  fromToday,
  className,
}: {
  now: number;
  editor: PersonId;
  tasks: Task[];
  busy: Busy[];
  onOpen?: (task: Task, el: HTMLElement) => void;
  onPropose?: (task: Task, day: number) => void;
  highlightId?: string;
  compact?: boolean;
  /** прошедшие дни не показываем — в них уже ничего не сделать */
  fromToday?: boolean;
  className?: string;
}) {
  const today = dayOf(now);
  const days = fromToday ? DAYS.filter((d) => d.idx >= today) : DAYS;
  const [hoverDay, setHoverDay] = useState<number | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const dragging = useRef(false);

  // Карточка при перетаскивании остаётся в DOM исходной колонки — её пропускаем и ищем колонку под ней
  const dayAt = (x: number, y: number, from: EventTarget | null) => {
    const self = (from as Element | null)?.closest?.("[data-card]") ?? null;
    for (const el of document.elementsFromPoint(x, y)) {
      if (self && self.contains(el)) continue;
      const col = (el as HTMLElement).closest?.("[data-day]") as HTMLElement | null;
      if (col) return Number(col.dataset.day);
    }
    return null;
  };

  return (
    <div className={cn("grid min-h-0 gap-2.5", className)} style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}>
      {days.map((d) => {
        const past = isPastDay(d.idx, now);
        const isToday = d.idx === today;
        const items = dayItems(editor, d.idx, tasks, busy);
        const count = dayTaskCount(editor, d.idx, tasks, busy);
        const order: Status[] = ["wait", "rework", "work", "review", "done"];
        const list = [...items.tasks].sort((a, b) => order.indexOf(a.status) - order.indexOf(b.status) || a.due - b.due);
        const dropOk = hoverDay === d.idx && !past;
        return (
          <section
            key={d.idx}
            data-day={d.idx}
            className={cn(
              "flex min-h-0 flex-col gap-2 rounded-[24px] p-2 transition-[background-color,box-shadow] duration-200",
              isToday ? "bg-work-soft/60 shadow-[inset_0_0_0_1px_var(--work-bd)]" : "bg-muted",
              past && "opacity-60",
              dropOk && "bg-review-soft shadow-[inset_0_0_0_2px_var(--review)]",
            )}
          >
            <header className="flex items-center gap-1.5 px-1.5 pt-1">
              <span className={cn("text-[11px] font-medium tracking-wider", isToday ? "text-foreground" : "text-muted-foreground")}>{d.short}</span>
              <span className={cn("grid size-7 place-items-center rounded-full text-sm", isToday ? "bg-primary font-semibold text-primary-foreground" : "font-medium")}>{d.date}</span>
              {isToday && <span className="text-xs font-medium text-work-fg">сегодня</span>}
            </header>
            <span className="px-1.5 text-[11px] font-medium text-muted-foreground">{tasksLabel(count)}</span>
            <div
              className={cn(
                "no-scrollbar flex min-h-0 flex-col gap-1.5",
                list.some((t) => t.id === dragId) ? "overflow-visible" : "overflow-y-auto overflow-x-hidden",
                compact ? "max-h-72" : "flex-1",
              )}
            >
              {list.map((t) => {
                const draggable = !!onPropose && t.status === "wait" && !t.proposal;
                return (
                  <motion.div
                    key={t.id}
                    data-card={t.id}
                    layoutId={`eday-${t.id}`}
                    layout
                    drag={draggable}
                    dragSnapToOrigin
                    dragMomentum={false}
                    whileDrag={{ scale: 1.04, zIndex: 30, boxShadow: "0 16px 40px rgb(10 10 10 / 0.18)" }}
                    onDragStart={() => {
                      dragging.current = true;
                      setDragId(t.id);
                    }}
                    onDrag={(ev) => {
                      const p = ev as PointerEvent;
                      setHoverDay(dayAt(p.clientX, p.clientY, ev.target));
                    }}
                    onDragEnd={(ev) => {
                      const p = ev as PointerEvent;
                      const target = dayAt(p.clientX, p.clientY, ev.target);
                      setHoverDay(null);
                      setDragId(null);
                      window.setTimeout(() => (dragging.current = false), 0);
                      if (target !== null && target !== t.day && !isPastDay(target, now)) onPropose?.(t, target);
                    }}
                    onClick={(ev) => !dragging.current && onOpen?.(t, ev.currentTarget as HTMLElement)}
                    transition={spring.gentle}
                    style={{ borderRadius: 16 }}
                    className={cn(
                      "relative flex cursor-pointer select-none flex-col gap-1 p-2.5 pl-3 text-left",
                      draggable ? "cursor-grab active:cursor-grabbing" : "",
                      CARD[t.status],
                      highlightId === t.id && "ring-2 ring-wait ring-offset-1",
                    )}
                  >
                    <span className={cn("line-clamp-2 font-semibold leading-snug", compact ? "text-xs" : "text-[13px]")}>{t.title}</span>
                    <span className="truncate text-[11px] text-muted-foreground">
                      {t.proposal ? (
                        <span className="font-medium text-review-fg">вы предложили {dayInfo(t.proposal.day).abbr.toLowerCase()}</span>
                      ) : (
                        <>
                          {WORD[t.status] && <span className="font-medium">{WORD[t.status]}</span>}
                          {(t.status === "work" || t.status === "wait" || t.status === "rework") && `${WORD[t.status] ? " · " : ""}до ${timeOf(t.due)}`}
                        </>
                      )}
                    </span>
                  </motion.div>
                );
              })}
              {items.proposals.map((t) => (
                <div
                  key={`p-${t.id}`}
                  onClick={(ev) => onOpen?.(t, ev.currentTarget as HTMLElement)}
                  className="flex cursor-pointer flex-col gap-1 rounded-[16px] bg-[repeating-linear-gradient(135deg,var(--review-soft)_0_7px,#ebe4ff_7px_14px)] p-2.5 pl-3 shadow-[inset_3px_0_0_0_var(--review)]"
                >
                  <span className="line-clamp-2 text-xs font-semibold text-review-fg">{t.title}</span>
                  <span className="text-[11px] text-review-fg/80">предложено сюда</span>
                </div>
              ))}
              {items.busy.map((b) => (
                <div
                  key={b.id}
                  className="flex items-center gap-2 rounded-[14px] bg-[repeating-linear-gradient(135deg,#efefef_0_6px,#e7e7e7_6px_12px)] px-2.5 py-2 text-[11px] font-medium text-muted-foreground"
                >
                  <span className="min-w-0 flex-1 truncate">{b.title}</span>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
